import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { CitadelNotificationService, CitadelErrorLoggingService } from '@citadel/core';
import { ForgotPasswordModel } from 'src/app/models/forgot-password.model';
import { RecaptchaService } from 'src/app/services/recaptcha.service';
import { SignInService } from 'src/app/services/sign-in-service';
import { logFormErrors, logWhenInvalid } from '../form-logging';

@Component({
  selector: 'app-sign-in-password-step',
  templateUrl: './sign-in-password-step.component.html',
  styleUrls: ['./sign-in-password-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SignInPasswordStepComponent {
  private http = inject(HttpClient);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private recaptchaService = inject(RecaptchaService);
  private notify = inject(CitadelNotificationService);
  private errorLogging = inject(CitadelErrorLoggingService);
  private signInService = inject(SignInService);

  private defaultErrorMessage: string = 'An error occurred. Please try again';
  protected passwordForm: FormGroup = this.fb.group({
    password: [this.signInService.password(), [Validators.required]]
  });
  protected passwordFormSubmitted: boolean = false;

  // convenience getter for easy access to form fields
  protected get pswForm() {
    return this.passwordForm.controls;
  }

  constructor() {
    logWhenInvalid(this.errorLogging, this.passwordForm, 'password');
  }

  protected goToSendOTP() {
    console.log('Submitting form');
    this.passwordFormSubmitted = true;
    // stop here if form is invalid
    if (this.passwordForm.invalid) {
      logFormErrors(this.errorLogging, this.passwordForm, 'password');
      this.notify.error('Please enter your password.', " ");
      return;
    }

    this.recaptchaService.execute('identifyClient').subscribe({
      next: () => {
        this.signInService.password.set(this.passwordForm.value.password);
        this.router.navigate(['/sign-in', 'otp-channel']);
      },
      error: () => {
        this.notify.error('Error verifying the request (reCaptcha v3)', " ");
      }
    });
  }

  protected beforeValidateEmail() {
    this.recaptchaService.execute('validateEmail').subscribe(token => this.validateEmail(token));
  }

  private validateEmail(recaptchaToken: string) {
    const email = this.signInService.email();
    if (!email.match(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/)) {
      return;
    }
    this.http.post<ForgotPasswordModel>(`/api/auth/forgotpassword`,
      { email, returnUrl: this.signInService.returnUrl(), recaptchaToken },
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true, }).subscribe({
        next: res => {
          if (res.passwordResetRequestId.match(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
            && res.otpRequestId.match(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)) {

            this.signInService.passwordResetRequestId.set(res.passwordResetRequestId);
            this.signInService.passwordResetEmail.set(email);
            this.signInService.otpResetRequestId = res.otpRequestId;
            this.router.navigateByUrl('/forgot-password');
          }
        }, error: _error => {
          this.notify.error(this.defaultErrorMessage, "");
        }
      });
  }
}
